import {useNavigate, Link} from "react-router-dom";
import {inject, observer} from "mobx-react";

import {withAuthentication} from "../components/AuthenticateUser";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Legend, Title, Tooltip } from 'chart.js';
Chart.register(BarController, BarElement, CategoryScale, LinearScale, Legend, Title, Tooltip);
import {RequestBuilder} from "../queries/request";
import {getConfig} from "../lib/configMgr";
import SpinnerAnimation from "../components/Spinner";
import AdoptionAnalytics from "../components/admin/AdoptionAnalytics";
import './Admin.css';
import officialLogo from '../img/official_logo.svg';
import { memberAddErrorMessage } from "./adminMemberErrors";

const {ExternalRESTAPI} = getConfig();


const Admin = ({userStore}) => {
  const {isAdmin, fetchUsage, authData} = userStore;
  const navigate = useNavigate();
  const [usage, setUsage] = useState({users: {}});
  const [status, setStatus] = useState("");
  const [llmDays, setLlmDays] = useState(7);
  const [includeAnalysis, setIncludeAnalysis] = useState(true);
  const [filterDomains, setFilterDomains] = useState("all");
  const [excludePlexLLM, setExcludePlexLLM] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [adminTab, setAdminTab] = useState('usage'); // 'usage' | 'analysis' | 'help' | 'users'
  const [summaryDays, setSummaryDays] = useState(7);
  const [summaryDaysInput, setSummaryDaysInput] = useState('7');
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [chartMode, setChartMode] = useState('both'); // 'searches' | 'conversations' | 'mcp' | 'both'
  const [originFilter, setOriginFilter] = useState('both'); // 'api' | 'direct' | 'both'
  const [sortCol, setSortCol] = useState(null); // 'user'|'searches'|'conversations'|'sc'|'total'
  const [sortDir, setSortDir] = useState('desc'); // 'asc'|'desc'
  const [excludePlex, setExcludePlex] = useState(true);
  const [selectedDomains, setSelectedDomains] = useState(null);
  const [pendingDomains, setPendingDomains] = useState(null);
  const [domainsExpanded, setDomainsExpanded] = useState(false);
  const summaryChartInstance = useRef(null);
  const [chartCanvas, setChartCanvas] = useState(null);
  const summaryChartRef = useCallback(node => setChartCanvas(node), []);
  const [members, setMembers] = useState([]);
  const [memberFilter, setMemberFilter] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [memberStatus, setMemberStatus] = useState('');
  const [memberStatusError, setMemberStatusError] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [newlyAddedEmail, setNewlyAddedEmail] = useState('');
  const memberListRef = useRef(null);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [confirmingEmail, setConfirmingEmail] = useState('');
  const [regStatus, setRegStatus] = useState('');
  const [regStatusError, setRegStatusError] = useState(false);
  const [showRegistered, setShowRegistered] = useState(false);
  const [registeredEmails, setRegisteredEmails] = useState(null);
  const [registeredLoading, setRegisteredLoading] = useState(false);
  const [registeredError, setRegisteredError] = useState('');

  useEffect(() => {
    if (!newlyAddedEmail || !memberListRef.current) return;
    const row = memberListRef.current.querySelector(`[data-email="${CSS.escape(newlyAddedEmail)}"]`);
    if (row) {
      row.scrollIntoView({block: 'nearest', behavior: 'smooth'});
      setNewlyAddedEmail('');
    }
  }, [members, newlyAddedEmail]);

  const fetchMembers = () => {
    const url = new URL(ExternalRESTAPI.GroupMembers, window.location.origin);
    new RequestBuilder(url)
      .withAuthorization(authData)
      .fetch()
      .then(r => r.json())
      .then(data => {
        setMembers(data.members || []);
        setMemberStatus('');
        setMemberStatusError(false);
      })
      .catch(err => {
        setMemberStatus(`Error loading members: ${err}`);
        setMemberStatusError(true);
        setMembers([]);
      });
  };

  const fetchPendingRegistrations = () => {
    const url = new URL(ExternalRESTAPI.AdminPendingRegistrations, window.location.origin);
    new RequestBuilder(url)
      .withAuthorization(authData)
      .fetch()
      .then(r => r.json())
      .then(data => {
        setPendingRegistrations(data.registrations || []);
        setRegStatus('');
        setRegStatusError(false);
      })
      .catch(err => {
        setRegStatus(`Error loading pending registrations: ${err}`);
        setRegStatusError(true);
        setPendingRegistrations([]);
      });
  };

  const fetchRegisteredEmails = () => {
    setRegisteredLoading(true);
    const url = new URL(ExternalRESTAPI.AdminUsersRegistered, window.location.origin);
    new RequestBuilder(url)
      .withAuthorization(authData)
      .fetch()
      .then(r => r.json())
      .then(data => {
        setRegisteredEmails(new Set((data.emails || []).map(e => e.toLowerCase())));
        setRegisteredError('');
      })
      .catch(err => {
        setRegisteredError(`Error loading registered users: ${err}`);
        setRegisteredEmails(null);
      })
      .finally(() => setRegisteredLoading(false));
  };

  const handleToggleShowRegistered = () => {
    const next = !showRegistered;
    setShowRegistered(next);
    if (next && registeredEmails === null && !registeredLoading) {
      fetchRegisteredEmails();
    }
  };

  const handleConfirmRegistration = (email) => {
    setConfirmingEmail(email);
    const url = new URL(ExternalRESTAPI.AdminConfirmRegistration, window.location.origin);
    new RequestBuilder(url)
      .withAuthorization(authData)
      .withJSONContentType()
      .asPOST(JSON.stringify({email}))
      .fetch()
      .then(() => {
        setPendingRegistrations(prev => prev.filter(r => r.email !== email));
        setRegStatus(`Confirmed ${email}`);
        setRegStatusError(false);
      })
      .catch(err => {
        setRegStatus(`Error confirming ${email}: ${err}`);
        setRegStatusError(true);
      })
      .finally(() => {
        setConfirmingEmail('');
      });
  };

  const handleAddMember = () => {
    const email = newMemberEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMemberStatus('Invalid email address');
      setMemberStatusError(true);
      return;
    }
    setAddingMember(true);
    const url = new URL(ExternalRESTAPI.GroupMembers, window.location.origin);
    new RequestBuilder(url)
      .withAuthorization(authData)
      .withJSONContentType()
      .asPOST(JSON.stringify({email}))
      .fetch()
      .then(() => {
        setNewMemberEmail('');
        setMemberStatus(`Added ${email}`);
        setMemberStatusError(false);
        setNewlyAddedEmail(email);
        fetchMembers();
      })
      .catch(err => {
        setMemberStatus(memberAddErrorMessage(err, email));
        setMemberStatusError(true);
      })
      .finally(() => {
        setAddingMember(false);
      });
  };

  const handleRemoveMember = (email) => {
    const url = new URL(`${ExternalRESTAPI.GroupMembers}/${encodeURIComponent(email)}`, window.location.origin);
    new RequestBuilder(url)
      .withAuthorization(authData)
      .asDELETE()
      .fetch()
      .then(() => {
        setMembers(prev => prev.filter(m => m !== email));
        setMemberStatus(`Removed ${email}`);
        setMemberStatusError(false);
      })
      .catch(err => {
        setMemberStatus(`Error: ${err}`);
        setMemberStatusError(true);
      });
  };

  const handleResetPassword = (email) => {
    if (!window.confirm(`Send password reset email to ${email}?`)) return;
    const url = new URL(`${ExternalRESTAPI.AdminUsers}/${encodeURIComponent(email)}/reset-password`, window.location.origin);
    new RequestBuilder(url)
      .withAuthorization(authData)
      .asPOST()
      .fetch()
      .then(() => {
        setMemberStatus(`Password reset sent to ${email}`);
        setMemberStatusError(false);
      })
      .catch(err => {
        // err.message already carries the backend's specific reason (e.g. "no account
        // exists for X — they must sign up first"); don't bury it under a generic prefix.
        setMemberStatus(err.message || `Error resetting password for ${email}`);
        setMemberStatusError(true);
      });
  };

  const handleDeleteUser = (email) => {
    if (!window.confirm(`Delete Cognito account for ${email}? This cannot be undone.`)) return;
    const url = new URL(`${ExternalRESTAPI.AdminUsers}/${encodeURIComponent(email)}`, window.location.origin);
    new RequestBuilder(url)
      .withAuthorization(authData)
      .asDELETE()
      .fetch()
      .then(() => {
        setMemberStatus(`Deleted account for ${email}`);
        setMemberStatusError(false);
      })
      .catch(err => {
        setMemberStatus(`Error deleting ${email}: ${err}`);
        setMemberStatusError(true);
      });
  };

  useEffect(() => {
    fetchMembers();
    fetchPendingRegistrations();
  }, []);

  useEffect(() => {
    handleRefresh();
  }, []);

  const handleRefresh = () => {
    setStatus("Fetching cache usage...");
    fetchUsage()
      .then((info) => {
        setUsage(info);
        setStatus("");
      })
      .catch(error => {
        setStatus(`Error: ${error}`);
      });
  }

  const handleGenerateLLMReport = async (source) => {
    const isHelp = source === 'help';
    const label = isHelp ? 'Help Analysis' : 'LLM Usage';
    setGenerating(true);
    setStatus(`Fetching ${label.toLowerCase()} data for last ${llmDays} days...`);

    // Open window IMMEDIATELY in the click handler to avoid popup blocker
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <html>
        <head>
          <title>Generating ${label} Report...</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; padding: 20px; background: #f5f5f5; line-height: 1.6; }
            .container { max-width: 600px; margin: 50px auto; background: white; padding: 40px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); text-align: center; }
            h2 { color: #333; margin-top: 0; }
            .spinner { border: 4px solid #f3f3f3; border-top: 4px solid #007bff; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 20px auto; }
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            p { color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <h2>Generating ${label} Report</h2>
            <div class="spinner"></div>
            <p id="status">Fetching ${label.toLowerCase()} data for last ${llmDays} days...</p>
            <p style="font-size: 12px; color: #999;">This may take 15-135 seconds depending on analysis options.</p>
          </div>
        </body>
        </html>
      `);
    }

    try {
      const fetchUrl = new URL(ExternalRESTAPI.UsageFetch, window.location.origin);
      fetchUrl.searchParams.set('days', llmDays.toString());
      if (isHelp) {
        fetchUrl.searchParams.set('source', 'help');
      } else if (selectedDomains !== null && selectedDomains.size < allDomains.length) {
        const included = [...selectedDomains].join(',');
        if (included) fetchUrl.searchParams.set('filter_domains', included);
        const excluded = allDomains.filter(d => !selectedDomains.has(d));
        if (excluded.length) fetchUrl.searchParams.set('exclude_domains', excluded.join(','));
      }

      const fetchResponse = await new RequestBuilder(fetchUrl)
        .withAuthorization(authData)
        .fetch();

      const promptsData = await fetchResponse.json();

      let data;

      if (includeAnalysis) {
        // Step 2: Analyze with Claude (30-90 seconds)
        setStatus(`Analyzing prompts with Claude (${llmDays} days of data)...`);

        // Update the loading window with new status
        if (newWindow && !newWindow.closed) {
          const statusEl = newWindow.document.getElementById('status');
          if (statusEl) {
            statusEl.textContent = `Analyzing prompts with Claude (${llmDays} days of data)...`;
          }
        }

        const analyzeUrl = new URL(ExternalRESTAPI.UsageAnalyze, window.location.origin);

        const analyzeResponse = await new RequestBuilder(analyzeUrl)
          .withAuthorization(authData)
          .withJSONContentType()
          .asPOST(JSON.stringify({
            prompts_data: promptsData,
            markdown: true,
            ...(isHelp && {source: 'help'})
          }))
          .fetch();

        const analysisData = await analyzeResponse.json();

        // Combine fetch data and analysis results
        data = {...promptsData, ...analysisData};
      } else {
        // No analysis requested, just use the fetch data
        data = promptsData;
      }

      // Check if markdown_report field exists, otherwise show JSON
      const hasMarkdown = !!data.markdown_report;
      const content = hasMarkdown ? data.markdown_report : JSON.stringify(data, null, 2);

      // Write final content to the window
      if (newWindow && !newWindow.closed) {
        const statsData = hasMarkdown ? data.statistics || {} : null;
        const escJson = (v) => JSON.stringify(v).replace(/<\/script>/gi, '<\\/script>');

        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>${label} Report - Last ${llmDays} Days</title>
   <script src="https://cdn.jsdelivr.net/npm/marked@11.1.1/marked.min.js" integrity="sha384-zbcZAIxlvJtNE3Dp5nxLXdXtXyxwOdnILY1TDPVmKFhl4r4nSUG1r8bcFXGVa4Te" crossorigin="anonymous" defer></script>
  <script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.js" integrity="sha384-FcQlsUOd0TJjROrBxhJdUhXTUgNJQxTMcxZe6nHbaEfFL1zjQ+bq/uRoBQxb0KMo" crossorigin="anonymous" defer></script>
  <script src="https://cdn.jsdelivr.net/npm/dompurify@3.2.4/dist/purify.min.js" integrity="sha384-eEu5CTj3qGvu9PdJuS+YlkNi7d2XxQROAFYOr59zgObtlcux1ae1Il3u7jvdCSWu" crossorigin="anonymous" defer></script>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; padding: 20px; background: #f5f5f5; line-height: 1.6; }
    .container { max-width: 1000px; margin: 0 auto; background: white; padding: 40px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    h1 { border-bottom: 3px solid #007bff; padding-bottom: 10px; margin-top: 0; }
    h2 { color: #333; border-bottom: 2px solid #eee; padding-bottom: 8px; margin-top: 30px; }
    h3 { color: #555; margin-top: 20px; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th, td { text-align: left; padding: 10px; border-bottom: 1px solid #eee; }
    th { background: #f8f9fa; font-weight: 600; }
    tr:hover { background: #f8f9fa; }
    code { background: #f8f9fa; padding: 2px 6px; border-radius: 3px; font-family: monospace; font-size: 0.9em; }
    pre { background: #f8f9fa; padding: 15px; border-radius: 4px; overflow-x: auto; }
    pre code { background: transparent; padding: 0; }
    ul, ol { margin: 10px 0; padding-left: 30px; }
    li { margin: 5px 0; }
    blockquote { border-left: 4px solid #007bff; margin: 15px 0; padding: 10px 20px; background: #f8f9fa; }
  </style>
</head>
<body>
  <div class="container">
    <div id="content">Loading...</div>
  </div>
  <script>
    window.statsData = ${escJson(statsData)};
    window.markdownContent = ${escJson(content)};

    // Wait for libraries to load
    function init() {
      if (typeof marked === 'undefined' || typeof Chart === 'undefined' || typeof DOMPurify === 'undefined') {
        setTimeout(init, 100);
        return;
      }

      console.log('Libraries loaded, rendering...');

      // Render markdown — sanitize with DOMPurify to prevent XSS from LLM-generated content
      marked.setOptions({ breaks: true, gfm: true });
      document.getElementById('content').innerHTML = DOMPurify.sanitize(marked.parse(window.markdownContent));

      // Create chart if data available
      if (window.statsData && window.statsData.daily_counts) {
        const chartCanvas = document.getElementById('dailyActivityChart');
        if (chartCanvas) {
          chartCanvas.width = 900;
          chartCanvas.height = 400;
          const dailyItems = Object.entries(window.statsData.daily_counts).sort();
          const dates = dailyItems.map(([date]) => date);

          // Check if we have per-user breakdown data
          const hasUserBreakdown = window.statsData.daily_user_counts &&
                                   Object.keys(window.statsData.daily_user_counts).length > 0;

          let datasets;
          if (hasUserBreakdown) {
            // Build per-user datasets for stacked chart
            const allUsers = window.statsData.users || [];
            function domainHashHue(domain) {
              var hash = 0;
              for (var i = 0; i < domain.length; i++) hash = domain.charCodeAt(i) + ((hash << 5) - hash);
              return ((hash % 360) + 360) % 360;
            }
            function getUserColor(email, users) {
              var domain = email.split('@')[1] || email;
              var hue = domainHashHue(domain);
              var domainUsers = users.filter(function(u) { return (u.split('@')[1] || u) === domain; }).sort();
              var idx = domainUsers.indexOf(email);
              var count = domainUsers.length;
              var lightness = count === 1 ? 50 : 35 + (idx * 30 / Math.max(count - 1, 1));
              var saturation = count === 1 ? 70 : 55 + (idx * 25 / Math.max(count - 1, 1));
              return 'hsla(' + hue + ', ' + saturation + '%, ' + lightness + '%, 0.8)';
            }

            datasets = allUsers.map(function(user) {
              var userData = dates.map(function(date) {
                var dayData = window.statsData.daily_user_counts[date];
                return dayData ? (dayData[user] || 0) : 0;
              });

              return {
                label: user.split('@')[0],
                data: userData,
                backgroundColor: getUserColor(user, allUsers),
                borderWidth: 1
              };
            });
          } else {
            // Fallback to simple total counts
            const counts = dailyItems.map(([, count]) => count);
            datasets = [{
              label: 'Conversations Started',
              data: counts,
              backgroundColor: 'rgba(54, 162, 235, 0.6)',
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 1
            }];
          }

          new Chart(chartCanvas, {
            type: 'bar',
            data: {
              labels: dates,
              datasets: datasets
            },
            options: {
              responsive: false,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: hasUserBreakdown,
                  position: 'bottom'
                },
                title: {
                  display: true,
                  text: hasUserBreakdown ? 'Daily Conversations Started by User' : 'Daily Conversations Started',
                  font: { size: 16 }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  stacked: hasUserBreakdown,
                  title: { display: true, text: 'Conversations Started' }
                },
                x: {
                  stacked: hasUserBreakdown,
                  title: { display: true, text: 'Date' }
                }
              }
            }
          });
          console.log('Chart created successfully');
        } else {
          console.error('Canvas element not found');
        }
      }
    }

    // Start initialization
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  </script>
</body>
</html>`;

        // Clear the existing "Generating..." content and write the final report
        newWindow.document.open();
        newWindow.document.write(htmlContent);
        newWindow.document.close();
        newWindow.focus();
        setStatus(`Report generated successfully for ${llmDays} days`);
      } else {
        setStatus('Error: Could not open new window. Please check popup blocker.');
      }
    } catch (error) {
      if (newWindow && !newWindow.closed) {
        newWindow.document.open();
        newWindow.document.write(`
          <html>
          <head>
            <title>Error Generating Report</title>
            <style>
              body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; padding: 20px; background: #f5f5f5; line-height: 1.6; }
              .container { max-width: 600px; margin: 50px auto; background: white; padding: 40px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
              h2 { color: #d9534f; margin-top: 0; }
              p { color: #666; }
              pre { background: #f8f9fa; padding: 15px; border-radius: 4px; overflow-x: auto; }
            </style>
          </head>
          <body>
            <div class="container">
              <h2>Error Generating Report</h2>
              <p>An error occurred while generating the LLM usage report:</p>
              <pre>${(error.message || String(error)).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
            </div>
          </body>
          </html>
        `);
        newWindow.document.close();
        newWindow.focus();
      }
      setStatus(`Error generating report: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  }

  const commitSummaryDays = (val) => {
    const clamped = Math.max(1, Math.min(180, parseInt(val, 10) || 7));
    setSummaryDaysInput(String(clamped));
    setSummaryDays(clamped);
  };

  const [fetchProgress, setFetchProgress] = useState({done: 0, total: 0});

  // Generic merge for /usage/fetch response chunks: numbers sum, arrays union
  // (deduping), objects merge key-by-key recursively. Covers every field shape
  // search_statistics actually uses (counts, per-user dicts, by_origin, etc).
  const mergeUsageChunks = (a, b) => {
    if (a === undefined) return b;
    if (b === undefined) return a;
    if (typeof a === 'number' && typeof b === 'number') return a + b;
    if (Array.isArray(a) && Array.isArray(b)) return [...new Set([...a, ...b])];
    if (a && b && typeof a === 'object' && typeof b === 'object') {
      const out = {...a};
      for (const k of Object.keys(b)) out[k] = mergeUsageChunks(out[k], b[k]);
      return out;
    }
    return b;
  };

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    setSummaryError('');
    setFetchProgress({done: 0, total: summaryDays});

    (async () => {
      // Fetch one day at a time (instead of one big days=N request) so the UI
      // can show real fetch progress, and so a wide range can't blow past the
      // gateway timeout the way a single all-at-once query can under load.
      let merged = null;
      for (let i = 0; i < summaryDays; i++) {
        if (cancelled) return;
        const url = new URL(ExternalRESTAPI.UsageFetch, window.location.origin);
        url.searchParams.set('days', '1');
        url.searchParams.set('end_days_ago', i.toString());
        url.searchParams.set('include_searches', 'true');
        try {
          const r = await new RequestBuilder(url).withAuthorization(authData).fetch();
          const data = await r.json();
          merged = merged === null ? data : mergeUsageChunks(merged, data);
        } catch (err) {
          if (!cancelled) setSummaryError(`Error: ${err}`);
        } finally {
          if (!cancelled) setFetchProgress(p => ({...p, done: i + 1}));
        }
      }
      if (!cancelled) {
        setSummaryData(merged);
        setSummaryLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [summaryDays, authData]);

  const getDomain = (email) => email.split('@')[1] || email;

  const allDomains = useMemo(() => {
    const base = summaryData?.search_statistics;
    if (!base) return [];
    const users = base.users || [];
    return [...new Set(users.map(getDomain))].sort();
  }, [summaryData]);

  const seenDomainsRef = useRef(new Set());

  useEffect(() => {
    if (allDomains.length === 0) return;

    if (selectedDomains === null) {
      allDomains.forEach(d => seenDomainsRef.current.add(d));
      const initial = new Set(allDomains);
      if (excludePlex) initial.delete('plexresearch.com');
      setSelectedDomains(initial);
      setPendingDomains(new Set(initial));
      return;
    }

    // A new date range or filter can surface domains that weren't in the
    // previously loaded window. Auto-include those (without re-adding ones
    // the user deliberately unchecked) so the domain filter doesn't stay
    // pinned to whatever domains happened to be present on first load.
    const newDomains = allDomains.filter(d => !seenDomainsRef.current.has(d));
    allDomains.forEach(d => seenDomainsRef.current.add(d));
    if (newDomains.length > 0) {
      const next = new Set(selectedDomains);
      newDomains.forEach(d => {
        if (d !== 'plexresearch.com' || !excludePlex) next.add(d);
      });
      setSelectedDomains(next);
      setPendingDomains(new Set(next));
    }
  }, [allDomains]);

  useEffect(() => {
    if (selectedDomains === null) return;
    const next = new Set(selectedDomains);
    if (excludePlex) {
      next.delete('plexresearch.com');
    } else if (allDomains.includes('plexresearch.com')) {
      next.add('plexresearch.com');
    }
    setSelectedDomains(next);
    setPendingDomains(new Set(next));
  }, [excludePlex, allDomains]);

  const domainHashHue = (domain) => {
    let hash = 0;
    for (let i = 0; i < domain.length; i++) {
      hash = domain.charCodeAt(i) + ((hash << 5) - hash);
    }
    return ((hash % 360) + 360) % 360;
  };

  const getUserColor = (email, allUsers) => {
    const domain = getDomain(email);
    const hue = domainHashHue(domain);
    const domainUsers = allUsers.filter(u => getDomain(u) === domain).sort();
    const idx = domainUsers.indexOf(email);
    const count = domainUsers.length;
    const lightness = count === 1 ? 50 : 35 + (idx * 30 / Math.max(count - 1, 1));
    const saturation = count === 1 ? 70 : 55 + (idx * 25 / Math.max(count - 1, 1));
    return `hsla(${hue}, ${saturation}%, ${lightness}%, 0.8)`;
  };

  const getStats = (data) => {
    const base = data?.search_statistics;
    if (!base) return null;
    let stats = originFilter === 'both' ? base
      : (base.by_origin?.[originFilter] ? { ...base.by_origin[originFilter], users: base.users } : base);
    if (selectedDomains !== null) {
      stats = { ...stats, users: (stats.users || []).filter(u => selectedDomains.has(getDomain(u))) };
    } else if (excludePlex) {
      stats = { ...stats, users: (stats.users || []).filter(u => !u.endsWith('@plexresearch.com')) };
    }
    return stats;
  };

  useEffect(() => {
    if (!chartCanvas || !summaryData?.search_statistics) return;

    const stats = getStats(summaryData);

    const allDates = Array.from(new Set([
      ...Object.keys(stats.daily_search_user_counts || {}),
      ...Object.keys(stats.daily_conversation_user_counts || {}),
      ...Object.keys(stats.daily_mcp_user_counts || {}),
    ])).sort();

    const users = stats.users || [];

    const getMergedCounts = (date, user) => {
      const s = (stats.daily_search_user_counts?.[date]?.[user] || 0);
      const c = (stats.daily_conversation_user_counts?.[date]?.[user] || 0);
      const m = (stats.daily_mcp_user_counts?.[date]?.[user] || 0);
      if (chartMode === 'searches') return s;
      if (chartMode === 'conversations') return c;
      if (chartMode === 'mcp') return m;
      return s + c + m;
    };

    const datasets = users.map((user) => ({
      label: user.split('@')[0],
      data: allDates.map(d => getMergedCounts(d, user)),
      backgroundColor: getUserColor(user, users),
      borderWidth: 1,
    }));

    if (summaryChartInstance.current) {
      summaryChartInstance.current.destroy();
    }

    summaryChartInstance.current = new Chart(chartCanvas, {
      type: 'bar',
      data: { labels: allDates, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: users.length > 0, position: 'bottom' },
          title: { display: true, text: 'Daily Activity by User', font: { size: 14 } },
        },
        scales: {
          y: { beginAtZero: true, stacked: true, title: { display: true, text: 'Events' } },
          x: { stacked: true, title: { display: true, text: 'Date' } },
        },
      },
    });

    return () => {
      if (summaryChartInstance.current) {
        summaryChartInstance.current.destroy();
        summaryChartInstance.current = null;
      }
    };
  }, [summaryData, chartMode, originFilter, chartCanvas, selectedDomains, excludePlex]);

  if (!isAdmin) {
    navigate("/", {replace:true});
  }
  document.title = "Plex Usage";
  return (
    <div className="admin-app" style={{padding: '20px', maxWidth: '1200px', margin: '0 auto'}}>
      <Link to="/"><img src={officialLogo} alt="Plex" className="admin-logo" /></Link>
      <h1>Plex Usage</h1>

      <div className="admin-tabs">
        {[['usage', 'Usage'], ['adoption', 'Adoption'], ['analysis', 'Analysis'], ['help', 'Help Analysis'], ['users', 'User Management']].map(([key, label]) => (
          <button key={key} className={adminTab === key ? 'active' : ''} onClick={() => setAdminTab(key)}>{label}</button>
        ))}
      </div>

      {adminTab === 'adoption' && <AdoptionAnalytics authData={authData} />}

      {adminTab === 'usage' && <section className="admin-analytics" style={{marginBottom: '20px'}}>
        <a
          href="https://analytics.google.com/analytics/web/#/a115077303p313249461/reports/intelligenthome?params=_u..nav%3Dmaui"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Analytics Dashboard
        </a>
        {" | "}
        <a
          href="https://us.posthog.com/project/374070"
          target="_blank"
          rel="noopener noreferrer"
        >
          PostHog Dashboard
        </a>
      </section>}

      {adminTab === 'usage' && <section style={{marginBottom: '30px'}}>
        <h2>Real-time Cache Usage (Last Hour)</h2>
        <p className="admin-section-desc">User request counts from Redis cache</p>
        <table className="admin-inline-table">
          <thead>
            <tr>
              <th style={{textAlign: 'left'}}>User</th>
              <th>Requests (last hour)</th>
              <th>Last active</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(usage.users || {}).map(([email, info]) => {
              const count = typeof info === 'object' ? info.count : info;
              const lastSeenAgo = typeof info === 'object' ? info.last_seen_ago : null;
              const lastActiveLabel = lastSeenAgo == null ? '—'
                : lastSeenAgo < 60 ? `${lastSeenAgo}s ago`
                : lastSeenAgo < 3600 ? `${Math.round(lastSeenAgo / 60)}m ago`
                : '> 1h ago';
              return (<tr key={email}>
                <td>{email}</td>
                <td>{count}</td>
                <td>{lastActiveLabel}</td>
              </tr>);
            })}
          </tbody>
        </table>
        <button onClick={handleRefresh} disabled={generating} style={{padding: '8px 16px', cursor: 'pointer'}}>
          Refresh Cache Usage
        </button>
      </section>}

      {adminTab === 'usage' && <section className="admin-usage-summary" style={{marginBottom: '30px'}}>
        <h2>Usage Summary</h2>
        <p className="admin-section-desc">Search and conversation launches per user from Graylog</p>
        <div className="usage-summary-controls">
          <label htmlFor="summary-days">Last</label>
          <input
            id="summary-days"
            type="number"
            min="1"
            max="180"
            value={summaryDaysInput}
            onChange={(e) => setSummaryDaysInput(e.target.value)}
            onBlur={(e) => commitSummaryDays(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitSummaryDays(e.target.value); }}
            disabled={summaryLoading}
            style={{width: '80px', padding: '6px'}}
          />
          <label>days</label>
          <div className="domain-filter-wrapper">
            <button
              className="domain-filter-toggle"
              disabled={summaryLoading}
              onClick={() => {
                if (!domainsExpanded) setPendingDomains(new Set(selectedDomains || []));
                setDomainsExpanded(!domainsExpanded);
              }}
            >
              Domains {selectedDomains ? `(${selectedDomains.size}/${allDomains.length})` : '(all)'} {domainsExpanded ? '▲' : '▼'}
            </button>
            {domainsExpanded && (
              <div className="domain-filter-dropdown">
                <div className="domain-filter-actions">
                  <button onClick={() => setPendingDomains(new Set(allDomains))}>Select all</button>
                  <button onClick={() => setPendingDomains(new Set())}>Deselect all</button>
                </div>
                <div className="domain-filter-list">
                  {allDomains.map(domain => (
                    <label key={domain} className="domain-filter-item">
                      <input
                        type="checkbox"
                        checked={pendingDomains?.has(domain) || false}
                        onChange={(e) => {
                          const next = new Set(pendingDomains);
                          if (e.target.checked) next.add(domain);
                          else next.delete(domain);
                          setPendingDomains(next);
                        }}
                      />
                      <span
                        className="domain-color-swatch"
                        style={{backgroundColor: `hsl(${domainHashHue(domain)}, 70%, 50%)`}}
                      />
                      {domain}
                    </label>
                  ))}
                </div>
                <button
                  className="domain-filter-apply"
                  onClick={() => {
                    setSelectedDomains(new Set(pendingDomains));
                    setExcludePlex(!pendingDomains.has('plexresearch.com') && allDomains.includes('plexresearch.com'));
                    setDomainsExpanded(false);
                  }}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
        {(() => {
          const stats = getStats(summaryData);
          const hasData = stats && (stats.users || []).length > 0;
          if (!hasData) return null;
          return (
            <>
              <div className="usage-chart-controls">
                <label htmlFor="chart-mode">Display</label>
                <select id="chart-mode" value={chartMode} onChange={(e) => setChartMode(e.target.value)} disabled={summaryLoading} style={{padding: '4px 8px'}}>
                  <option value="searches">Searches</option>
                  <option value="conversations">Conversations</option>
                  <option value="mcp">MCP</option>
                  <option value="both">All</option>
                </select>
                <label htmlFor="origin-filter">Origin</label>
                <select id="origin-filter" value={originFilter} onChange={(e) => setOriginFilter(e.target.value)} disabled={summaryLoading} style={{padding: '4px 8px'}}>
                  <option value="both">API + Direct</option>
                  <option value="api">API</option>
                  <option value="direct">Direct</option>
                </select>
              </div>
              {summaryLoading && (
                <div style={{marginBottom: '12px'}}>
                  <div style={{
                    height: '8px', borderRadius: '4px', background: '#e0e0e0',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${fetchProgress.total ? (fetchProgress.done / fetchProgress.total) * 100 : 0}%`,
                      background: '#007bff',
                      transition: 'width 0.2s ease-out'
                    }} />
                  </div>
                  <div className="admin-hint-text" style={{marginTop: '4px', fontSize: '12px'}}>
                    Fetched {fetchProgress.done} of {fetchProgress.total} day{fetchProgress.total === 1 ? '' : 's'}...
                  </div>
                </div>
              )}
              <div style={{height: '350px', position: 'relative', marginBottom: '16px', opacity: summaryLoading ? 0.35 : 1, transition: 'opacity 0.3s'}}>
                <canvas ref={summaryChartRef} />
              </div>
            </>
          );
        })()}
        {(() => {
          const stats = getStats(summaryData);
          const allUsers = stats?.users || [];
          const hasData = !summaryLoading && !summaryError && stats && allUsers.length > 0;

          const handleSort = (col) => {
            if (sortCol === col) {
              if (sortDir === 'desc') setSortDir('asc');
              else { setSortCol(null); setSortDir('desc'); }
            } else {
              setSortCol(col);
              setSortDir('desc');
            }
          };
          const sortIndicator = (col) => sortCol === col ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';

          const fmtTokens = (n) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}k` : String(n);
          const tokCell = (n) => <td style={{padding: '8px'}} title={n ? `${n.toLocaleString()} tokens` : ''}>{n ? fmtTokens(n) : ''}</td>;
          const apiStats = summaryData?.search_statistics?.by_origin?.api;
          const apiCallsFor = (u) => (apiStats?.user_search_counts?.[u] || 0)
            + (apiStats?.user_conversation_counts?.[u] || 0)
            + (apiStats?.user_search_conversation_counts?.[u] || 0)
            + (apiStats?.user_mcp_counts?.[u] || 0);
          let totals = {s: 0, c: 0, sc: 0, m: 0, api: 0, inTok: 0, outTok: 0, cacheRead: 0, cacheWrite: 0};
          let userRows = [];
          if (hasData) {
            const tc = stats.user_token_counts || {};
            const rows = allUsers.map(u => {
              const s = stats.user_search_counts?.[u] || 0;
              const c = stats.user_conversation_counts?.[u] || 0;
              const sc = stats.user_search_conversation_counts?.[u] || 0;
              const m = stats.user_mcp_counts?.[u] || 0;
              const api = apiCallsFor(u);
              const ut = tc[u] || {};
              const inTok = ut.input_tokens || 0;
              const outTok = ut.output_tokens || 0;
              const cacheRead = ut.cache_read_tokens || 0;
              const cacheWrite = ut.cache_write_tokens || 0;
              totals.s += s; totals.c += c; totals.sc += sc; totals.m += m; totals.api += api;
              totals.inTok += inTok; totals.outTok += outTok; totals.cacheRead += cacheRead; totals.cacheWrite += cacheWrite;
              return {u, s, c, sc, m, api, inTok, outTok, cacheRead, cacheWrite, total: s + c + sc + m};
            });
            if (sortCol) {
              const key = sortCol === 'user' ? 'u' : sortCol === 'searches' ? 's' : sortCol === 'conversations' ? 'c' : sortCol === 'sc' ? 'sc' : sortCol === 'mcp' ? 'm' : sortCol === 'api' ? 'api' : sortCol === 'inTok' ? 'inTok' : sortCol === 'outTok' ? 'outTok' : sortCol === 'cacheRead' ? 'cacheRead' : sortCol === 'cacheWrite' ? 'cacheWrite' : 'total';
              rows.sort((a, b) => {
                const av = a[key], bv = b[key];
                const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
                return sortDir === 'asc' ? cmp : -cmp;
              });
            }
            userRows = rows.map(({u, s, c, sc, m, api, inTok, outTok, cacheRead, cacheWrite, total}) => (
              <tr key={u}>
                <td style={{padding: '8px'}}>{u}</td>
                <td style={{padding: '8px'}}>{s}</td>
                <td style={{padding: '8px'}}>{c}</td>
                <td style={{padding: '8px'}}>{sc}</td>
                <td style={{padding: '8px'}}>{m}</td>
                <td style={{padding: '8px'}}>{api}</td>
                {tokCell(inTok)}
                {tokCell(outTok)}
                {tokCell(cacheRead)}
                {tokCell(cacheWrite)}
                <td style={{padding: '8px'}}>{total}</td>
              </tr>
            ));
          }

          const columnTooltips = {
            user: 'User email address',
            searches: 'Mutually exclusive event type: standalone search, not tied to any LLM conversation. Not a superset of Search Conv. — a user can do all their searching via chat and show 0 here.',
            conversations: 'Mutually exclusive event type: LLM chat with no search attached',
            sc: 'Mutually exclusive event type: LLM chat with a search attached (the search itself is not double-counted in "Searches")',
            mcp: 'Mutually exclusive event type: MCP tool invocation (resolve, search, ask, etc.) — always API origin, no direct/webapp MCP',
            api: 'Total calls made via API key (searches + conversations + search-conv + MCP with API origin), independent of the Origin filter above',
            inTok: 'LLM input tokens',
            outTok: 'LLM output tokens',
            cacheRead: 'Cached input tokens read (reduced cost)',
            cacheWrite: 'Cached input tokens written',
            total: 'Sum of all activity counts. Origin (API key vs. direct webapp session) is tracked separately per-event but merged in this table.',
          };
          const headerRow = (
            <tr>
              {[['user','User'],['searches','Searches'],['conversations','Conversations'],['sc','Search Conv.'],['mcp','MCP'],['api','API'],['inTok','In Tok'],['outTok','Out Tok'],['cacheRead','Cache Read'],['cacheWrite','Cache Write'],['total','Total']].map(([col, label]) => (
                <th key={col} data-sortable onClick={() => handleSort(col)} title={columnTooltips[col]}>{label}{sortIndicator(col)}</th>
              ))}
            </tr>
          );

          return (
            <div className="usage-table-container">
              <table>
                <thead>{headerRow}</thead>
                <tbody>{
                  summaryLoading ? (
                    <tr><td colSpan={11} style={{padding: '8px'}} className="admin-hint-text">
                      Fetched {fetchProgress.done} of {fetchProgress.total} day{fetchProgress.total === 1 ? '' : 's'}...
                    </td></tr>
                  ) : summaryError ? (
                    <tr><td colSpan={11} style={{padding: '8px', color: 'var(--danger-color, #c00)'}}>{summaryError}</td></tr>
                  ) : !hasData ? (
                    <tr><td colSpan={11} style={{padding: '8px'}} className="admin-hint-text">No data yet for this period</td></tr>
                  ) : userRows
                }</tbody>
                {hasData && <tfoot>
                  <tr className="usage-totals-row">
                    <td style={{padding: '8px'}}>Total</td>
                    <td style={{padding: '8px'}}>{totals.s}</td>
                    <td style={{padding: '8px'}}>{totals.c}</td>
                    <td style={{padding: '8px'}}>{totals.sc}</td>
                    <td style={{padding: '8px'}}>{totals.m}</td>
                    <td style={{padding: '8px'}}>{totals.api}</td>
                    {tokCell(totals.inTok)}
                    {tokCell(totals.outTok)}
                    {tokCell(totals.cacheRead)}
                    {tokCell(totals.cacheWrite)}
                    <td style={{padding: '8px'}}>{totals.s + totals.c + totals.sc + totals.m}</td>
                  </tr>
                </tfoot>}
              </table>
            </div>
          );
        })()}
      </section>}

      {adminTab === 'analysis' && <section style={{marginBottom: '30px'}}>
        <h2>LLM Usage Report</h2>
        <p className="admin-section-desc">Generate historical LLM prompt statistics from Graylog</p>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap'}}>
          <label htmlFor="llm-days">Last</label>
          <input
            id="llm-days"
            type="number"
            min="1"
            max="180"
            value={llmDays}
            onChange={(e) => setLlmDays(Math.max(1, Math.min(180, parseInt(e.target.value, 10) || 7)))}
            style={{width: '80px', padding: '6px'}}
            disabled={generating}
          />
          <label htmlFor="llm-days">days</label>
          <label style={{display: 'flex', alignItems: 'center', gap: '5px', marginLeft: '20px'}}>
            <input
              type="checkbox"
              checked={includeAnalysis}
              onChange={(e) => setIncludeAnalysis(e.target.checked)}
              disabled={generating}
            />
            Analyze prompts
          </label>
          <span className="admin-hint-text" style={{marginLeft: '5px'}}>
            Uses domain filter from Usage tab ({selectedDomains ? `${selectedDomains.size} domain${selectedDomains.size !== 1 ? 's' : ''}` : 'all'})
          </span>
          <button
            onClick={() => handleGenerateLLMReport()}
            disabled={generating}
            style={{padding: '8px 16px', cursor: generating ? 'wait' : 'pointer'}}
          >
            {generating ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
        <p className="admin-hint-text" style={{fontStyle: 'italic'}}>
          Report will open in a new tab. Takes 15-45 seconds without analysis, 45-135 seconds with Claude analysis.
        </p>
      </section>}

      {adminTab === 'help' && <section style={{marginBottom: '30px'}}>
        <h2>Help Widget Analysis</h2>
        <p className="admin-section-desc">Analyze pre-login help widget conversations from S3</p>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap'}}>
          <label htmlFor="help-days">Last</label>
          <input
            id="help-days"
            type="number"
            min="1"
            max="180"
            value={llmDays}
            onChange={(e) => setLlmDays(Math.max(1, Math.min(180, parseInt(e.target.value, 10) || 7)))}
            style={{width: '80px', padding: '6px'}}
            disabled={generating}
          />
          <label htmlFor="help-days">days</label>
          <label style={{display: 'flex', alignItems: 'center', gap: '5px', marginLeft: '20px'}}>
            <input
              type="checkbox"
              checked={includeAnalysis}
              onChange={(e) => setIncludeAnalysis(e.target.checked)}
              disabled={generating}
            />
            Analyze prompts
          </label>
          <button
            onClick={() => handleGenerateLLMReport('help')}
            disabled={generating}
            style={{padding: '8px 16px', cursor: generating ? 'wait' : 'pointer'}}
          >
            {generating ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
        <p className="admin-hint-text" style={{fontStyle: 'italic'}}>
          Report will open in a new tab. Scans S3 conversation archive — takes 10-30 seconds to fetch, plus 15-45 seconds for Claude analysis.
        </p>
      </section>}

      {adminTab === 'users' && pendingRegistrations.length > 0 && (
        <section className="pending-registrations" style={{marginBottom: '30px'}}>
          <h2>Pending Confirmations</h2>
          <p className="admin-section-desc">
            These users have registered but not confirmed their email. If a user&apos;s email
            system is filtering the registration message, they may be manually confirmed
            below. Use the copy button to send the SES delivery record to the user&apos;s IT
            department.
          </p>
          {regStatus && (
            <div className={`admin-member-status ${regStatusError ? 'error' : 'success'}`}>
              {regStatus}
            </div>
          )}
          <table className="admin-inline-table">
            <thead>
              <tr>
                <th style={{textAlign: 'left'}}>Email</th>
                <th style={{textAlign: 'left'}}>Registered</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pendingRegistrations.map(reg => {
                const copyText = reg.ses_notification
                  ? JSON.stringify(reg.ses_notification, null, 2)
                  : `User ${reg.email} registered at ${reg.created_at}. Plex Research sent a verification email from registration@plexresearch.com. Please whitelist this sender and the domain plexresearch.com.`;
                const registeredDate = new Date(reg.created_at).toLocaleDateString();
                const isConfirming = confirmingEmail === reg.email;
                return (
                  <tr key={reg.email}>
                    <td style={{padding: '8px'}}>{reg.email}</td>
                    <td style={{padding: '8px'}}>{registeredDate}</td>
                    <td style={{padding: '8px'}}>
                      <button
                        className="member-action-btn pending-reg-copy-btn"
                        title={reg.ses_notification ? 'Copy SES delivery record' : 'Copy IT department message'}
                        onClick={() => navigator.clipboard.writeText(copyText)}
                      >
                        <span className="glyphicon glyphicon-copy" />
                      </button>
                    </td>
                    <td style={{padding: '8px'}}>
                      <button
                        className="pending-reg-confirm-btn"
                        disabled={!!confirmingEmail}
                        onClick={() => handleConfirmRegistration(reg.email)}
                      >
                        {isConfirming ? 'Confirming…' : 'Confirm'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {adminTab === 'users' && <section style={{marginBottom: '30px'}}>
        <h2>User Management</h2>
        <p className="admin-section-desc">Manage members of the plex-search-users Google group</p>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px', flexWrap: 'wrap'}}>
          <input
            type="text"
            placeholder="Filter by email..."
            value={memberFilter}
            onChange={(e) => setMemberFilter(e.target.value)}
            style={{padding: '6px', width: '250px'}}
          />
          <input
            type="text"
            placeholder="new.user@example.com"
            value={newMemberEmail}
            onChange={(e) => setNewMemberEmail(e.target.value)}
            disabled={addingMember}
            style={{padding: '6px', width: '250px'}}
          />
          <button
            onClick={handleAddMember}
            disabled={!newMemberEmail.trim() || addingMember}
            style={{padding: '8px 16px', cursor: addingMember ? 'wait' : 'pointer'}}
          >
            {addingMember ? 'Adding…' : 'Add'}
            <SpinnerAnimation style={{float: 'right', display: addingMember ? '' : 'none'}} />
          </button>
          <label style={{display: 'flex', alignItems: 'center', gap: '5px', marginLeft: '10px'}}>
            <input
              type="checkbox"
              checked={showRegistered}
              onChange={handleToggleShowRegistered}
              disabled={registeredLoading}
            />
            Show registered (Cognito) status
          </label>
        </div>
        {memberStatus && (
          <div className={`admin-member-status ${memberStatusError ? 'error' : 'success'}`}>
            {memberStatus}
          </div>
        )}
        {showRegistered && registeredLoading && (
          <div className="admin-hint-text" style={{marginBottom: '10px'}}>Loading registration status…</div>
        )}
        {showRegistered && registeredError && (
          <div className="admin-member-status error" style={{marginBottom: '10px'}}>{registeredError}</div>
        )}
        <div ref={memberListRef} style={{maxHeight: '400px', overflowY: 'auto'}}>
          <table style={{width: '100%', borderCollapse: 'collapse'}}>
            <tbody>
              {members
                .filter(m => !memberFilter || m.toLowerCase().includes(memberFilter.toLowerCase()))
                .map(email => {
                  const isRegistered = registeredEmails?.has(email.toLowerCase());
                  return (
                  <tr key={email} data-email={email} className="member-row">
                    <td style={{padding: '8px'}}>
                      {email}
                      {showRegistered && registeredEmails !== null && (
                        <span
                          className={`registration-badge ${isRegistered ? 'registered' : 'unregistered'}`}
                          style={{
                            marginLeft: '8px',
                            fontSize: '11px',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            color: '#fff',
                            background: isRegistered ? '#28a745' : '#999',
                          }}
                        >
                          {isRegistered ? 'Registered' : 'Not registered'}
                        </span>
                      )}
                      <span className="member-actions">
                        <button className="member-action-btn" title="Copy to clipboard" onClick={() => navigator.clipboard.writeText(email)}>
                          <span className="glyphicon glyphicon-copy" />
                        </button>
                        <button className="member-action-btn" title="Reset password" onClick={() => handleResetPassword(email)}>
                          <span className="glyphicon glyphicon-lock" />
                        </button>
                        <button className="member-action-btn" title="Delete account" onClick={() => handleDeleteUser(email)}>
                          <span className="glyphicon glyphicon-remove-sign" />
                        </button>
                        <button className="member-action-btn" title="Remove from group" onClick={() => handleRemoveMember(email)}>
                          <span className="glyphicon glyphicon-trash" />
                        </button>
                      </span>
                    </td>
                  </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </section>}

      {status && (
        <div className={`admin-status-banner ${status.startsWith('Error') ? 'error' : 'success'}`}>
          <strong>Status:</strong> {status}
        </div>
      )}
    </div>
  )
};

export default withAuthentication(inject("userStore")(observer(Admin)));
